import Button from '@/components/base/Button';

interface InvoiceBulkBarProps {
  count: number;
  totalLabel: string | null;
  approvableCount: number;
  canApprove: boolean;
  onApprove: () => void;
  onReject: () => void;
  onClear: () => void;
}

export default function InvoiceBulkBar({
  count,
  totalLabel,
  approvableCount,
  canApprove,
  onApprove,
  onReject,
  onClear,
}: InvoiceBulkBarProps) {
  if (count === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
      {/* Slides up when the first invoice is ticked; a burgundy edge and a real shadow so it reads as floating. */}
      <div className="bulk-bar pointer-events-auto relative flex w-full max-w-3xl flex-col gap-3 overflow-hidden rounded-xl border border-background-200 bg-background-50/95 px-4 py-3 pl-5 shadow-[0_24px_50px_-20px_rgba(60,0,30,0.45)] backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <span className="absolute inset-y-0 left-0 w-1 bg-primary-500" aria-hidden="true" />
        <div className="flex items-center gap-3">
          <span key={count} className="flex h-9 w-9 animate-pop items-center justify-center rounded-md bg-primary-500 font-heading text-sm font-bold text-background-50">
            {count}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground-950">
              {count} invoice{count === 1 ? '' : 's'} selected
              {totalLabel ? <span className="font-normal text-foreground-500"> · {totalLabel}</span> : null}
            </p>
            {/* How many of the selection can be approved now. */}
            <div className="mt-1 flex items-center gap-2">
              <span className="h-1.5 w-28 overflow-hidden rounded-full bg-background-200" aria-hidden="true">
                <span
                  className="block h-full rounded-full bg-accent-500 transition-[width] duration-500 ease-out"
                  style={{ width: `${(approvableCount / count) * 100}%` }}
                />
              </span>
              <span className="text-xs text-foreground-500">
                {approvableCount} of {count} ready to approve{totalLabel ? '' : ' · mixed currencies'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" icon="ri-close-line" onClick={onClear}>
            Clear
          </Button>
          <Button variant="outline" size="sm" icon="ri-close-circle-line" onClick={onReject} disabled={!canApprove}>
            Reject
          </Button>
          <Button size="sm" icon="ri-check-double-line" className="join-shine" onClick={onApprove} disabled={!canApprove}>
            Approve selected
          </Button>
        </div>
      </div>
    </div>
  );
}